import { useEffect, useState } from 'react';
import {
  type AtmosphereSettings,
  useAtmosphereSettingsStore,
} from '../stores/atmosphereSettingsStore';

/**
 * A9 (2026-06-04) — derived atmosphere-settings hook.
 *
 * Layers OS-level overrides on top of the user-controlled toggles in
 * `useAtmosphereSettingsStore`:
 *   - `prefers-reduced-motion: reduce` forces `animationsEnabled` to
 *     false unconditionally. Per design §A9, the user cannot defeat
 *     this override; the settings panel surfaces a notice instead.
 *
 * Future Slice B will also fold in headphones-out detection
 * (`navigator.mediaDevices.enumerateDevices()` event listener) to
 * auto-mute SFX when the last audio output disconnects. Slice A
 * ships only the prefers-reduced-motion override.
 *
 * Returns the FULL settings object with the OS overrides applied,
 * plus the action callbacks from the store so consumers can wire
 * UI controls without two separate hooks.
 *
 * Components consuming this hook should NOT cache the returned
 * values — re-subscribe each render so OS state changes propagate.
 */
export interface UseAtmosphereSettingsResult extends AtmosphereSettings {
  /** True when the OS `prefers-reduced-motion: reduce` query matches.
   *  Forces `animationsEnabled = false` regardless of user setting. */
  prefersReducedMotion: boolean;
  setSetting: <K extends keyof AtmosphereSettings>(
    key: K,
    value: AtmosphereSettings[K],
  ) => void;
  applyMinimalPreset: () => void;
  resetToDefaults: () => void;
}

function readMatchMedia(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
}

export function useAtmosphereSettings(): UseAtmosphereSettingsResult {
  // INDIVIDUAL selectors per field — returning a new object from a
  // single combined selector triggers Zustand v4's infinite-loop
  // detection because each render produces a fresh reference. The
  // verbose per-field pattern is the cross-version safe idiom (works
  // in zustand v4 + v5 without needing `shallow` / `useShallow`).
  const animationsEnabledRaw = useAtmosphereSettingsStore((s) => s.animationsEnabled);
  const sfxEnabled = useAtmosphereSettingsStore((s) => s.sfxEnabled);
  const gunBarrelOpenerEnabled = useAtmosphereSettingsStore((s) => s.gunBarrelOpenerEnabled);
  const themeMusicTier = useAtmosphereSettingsStore((s) => s.themeMusicTier);
  const diceTumbleEnabled = useAtmosphereSettingsStore((s) => s.diceTumbleEnabled);
  const voiceTumbleDifferentialEnabled = useAtmosphereSettingsStore(
    (s) => s.voiceTumbleDifferentialEnabled,
  );
  const voiceConfirmStingEnabled = useAtmosphereSettingsStore(
    (s) => s.voiceConfirmStingEnabled,
  );
  const diceTumbleSfxVolume = useAtmosphereSettingsStore((s) => s.diceTumbleSfxVolume);
  const qrCinematicsEnabled = useAtmosphereSettingsStore((s) => s.qrCinematicsEnabled);
  const qrBrassSwellEnabled = useAtmosphereSettingsStore((s) => s.qrBrassSwellEnabled);
  const qrFlavorStringsEnabled = useAtmosphereSettingsStore(
    (s) => s.qrFlavorStringsEnabled,
  );
  const villainEntranceStingsEnabled = useAtmosphereSettingsStore(
    (s) => s.villainEntranceStingsEnabled,
  );
  const moduleSkinsEnabled = useAtmosphereSettingsStore((s) => s.moduleSkinsEnabled);
  const heroPointChipEnabled = useAtmosphereSettingsStore((s) => s.heroPointChipEnabled);
  const qBranchVoiceOverEnabled = useAtmosphereSettingsStore(
    (s) => s.qBranchVoiceOverEnabled,
  );
  const dossierRedactionEnabled = useAtmosphereSettingsStore(
    (s) => s.dossierRedactionEnabled,
  );
  // A8r Slice C (2026-06-04)
  const fameRecognitionCinematicEnabled = useAtmosphereSettingsStore(
    (s) => s.fameRecognitionCinematicEnabled,
  );
  // A8r Slice D (2026-06-04)
  const manualBadgeVisibleEnabled = useAtmosphereSettingsStore(
    (s) => s.manualBadgeVisibleEnabled,
  );
  // 2026-06-05 cinematic-linger
  const cinematicDismissMode = useAtmosphereSettingsStore(
    (s) => s.cinematicDismissMode,
  );
  const cinematicHoldMs = useAtmosphereSettingsStore((s) => s.cinematicHoldMs);
  // 2026-06-05 round 9 dice-feedback
  const dicePalette = useAtmosphereSettingsStore((s) => s.dicePalette);
  // Round 14 (2026-06-06, Sprint 2 §4.3)
  const diceChipFlyEnabled = useAtmosphereSettingsStore((s) => s.diceChipFlyEnabled);
  // Sprint 3 §4.2 (2026-06-06)
  const diceTheme = useAtmosphereSettingsStore((s) => s.diceTheme);
  // Sprint 4 §4.5 (2026-06-06)
  const voiceDebugEnabled = useAtmosphereSettingsStore((s) => s.voiceDebugEnabled);
  const setSetting = useAtmosphereSettingsStore((s) => s.setSetting);
  const applyMinimalPreset = useAtmosphereSettingsStore((s) => s.applyMinimalPreset);
  const resetToDefaults = useAtmosphereSettingsStore((s) => s.resetToDefaults);

  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(() =>
    readMatchMedia('(prefers-reduced-motion: reduce)'),
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (ev: MediaQueryListEvent) => {
      setPrefersReducedMotion(ev.matches);
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Legacy Safari API.
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  // Compose the OS override into animationsEnabled. The user-controlled
  // value is preserved on the store; the override applies only at the
  // hook layer so the settings panel can still show the user's choice
  // alongside the "Motion reduced by OS" notice.
  const animationsEnabled = animationsEnabledRaw && !prefersReducedMotion;

  return {
    animationsEnabled,
    sfxEnabled,
    gunBarrelOpenerEnabled,
    themeMusicTier,
    diceTumbleEnabled,
    voiceTumbleDifferentialEnabled,
    voiceConfirmStingEnabled,
    diceTumbleSfxVolume,
    qrCinematicsEnabled,
    qrBrassSwellEnabled,
    qrFlavorStringsEnabled,
    villainEntranceStingsEnabled,
    moduleSkinsEnabled,
    heroPointChipEnabled,
    qBranchVoiceOverEnabled,
    dossierRedactionEnabled,
    fameRecognitionCinematicEnabled,
    manualBadgeVisibleEnabled,
    cinematicDismissMode,
    cinematicHoldMs,
    dicePalette,
    diceChipFlyEnabled,
    diceTheme,
    voiceDebugEnabled,
    prefersReducedMotion,
    setSetting,
    applyMinimalPreset,
    resetToDefaults,
  };
}
